'use client';

import { SWRConfig } from 'swr';

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000; // 30 seconds

// Оптимизирана функция за fetch с error handling и timeout
const fetcher = async (url, options = {}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      signal: controller.signal,
      ...options,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
      error.info = await response.json().catch(() => ({}));
      error.status = response.status;
      throw error;
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);

    // Handle abort errors
    if (error.name === 'AbortError') {
      const timeoutError = new Error('Request timed out');
      timeoutError.status = 408;
      throw timeoutError;
    }

    // Log network errors for debugging
    // Note: Network errors are handled by throwing the error
    // No need to log here as they will be caught by SWR's error handling
    throw error;
  }
};

// Оптимизирана SWR конфигурация
// iOS 20+ safe: Disable aggressive revalidation to prevent hydration mismatch
const swrConfig = {
  fetcher,

  // Глобални настройки - ще бъдат override-нати от hooks
  revalidateOnFocus: false,
  revalidateOnReconnect: true, // Revalidate when internet comes back (PWA offline support)
  revalidateIfStale: false,

  // Retry настройки
  errorRetryCount: 2, // Намален брой опити
  errorRetryInterval: 3000, // По-бърз retry

  // iOS 20+ safe: Prevent aggressive retry on errors
  shouldRetryOnError: (error) => {
    // Don't retry on 401/403 errors (auth issues)
    const UNAUTHORIZED_STATUS = 401;
    const FORBIDDEN_STATUS = 403;
    if (error?.status === UNAUTHORIZED_STATUS || error?.status === FORBIDDEN_STATUS) {
      return false;
    }
    return true;
  },

  // Error handling
  onError: (_error, _key) => {
    // Не log-ваме 401/403 грешки за неавтентифицирани заявки
    // Те се обработват от shouldRetryOnError
  },

  // Success handling
  onSuccess: (_data, _key) => {
    // Success handler - може да се използва за logging или analytics в бъдеще
    // Понастоящем не се изисква никакво действие
  },

  // Loading states
  loadingTimeout: 10000, // 10 секунди timeout
  focusThrottleInterval: 5000, // Throttle focus revalidation
};

export default function SWRProvider({ children }) {
  return <SWRConfig value={swrConfig}>{children}</SWRConfig>;
}
